import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  ATLEAST_ONE_CAPITAL_REGEX,
  ATLEAST_ONE_NUMBER_REGEX,
  ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  ATLEAST_ONE_SMALL_REGEX,
  PHONE_NO_LENGTH,
  EMAIL_REGEX,
  TITLE_MAX_LIMIT,
  TEXTAREA_MAX_LENGTH,
  TABLE_CAPACITY_MAX,
  MIN_RESERVATION_DURATION_MINUTES,
} from "../../../utils/Constants"
import { PHONE_VALIDATION } from "../../../utils/commonValidations";

export const useValidationSchemas = () => {
  const { t } = useTranslation();

  const loginValidationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email(t("label_email_invalid_format_error"))
      .test('no-uppercase', t("label_email_uppercase_error"), function (value) {
        if (value) {
          return !/[A-Z]/.test(value);
        }
        return true;
      })
      .required(t("label_email_required_error")),
    password: Yup.string().required(t("validation_password_required"))
  })

  const registerValidationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email(t("label_email_invalid_format_error"))
      .test('no-uppercase', t("label_email_uppercase_error"), function (value) {
        if (value) {
          return !/[A-Z]/.test(value);
        }
        return true;
      })
      .required(t("label_email_required_error")),
    restaurant_name: Yup.string().required(t("label_restaurants_error"))
  })

  const forgetPasswordValidationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email("label_email_invalid_format_error")
      .test('no-uppercase', "label_email_uppercase_error", function (value) {
        if (value) {
          return !/[A-Z]/.test(value);
        }
        return true;
      })
      .required("label_email_required_error"),
  })

  const resetPswValidationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required(t("label_new_password_error"))
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("Password_Validation_maximum_length")
      ),
    confirmPassword: Yup.string()
      .required(t("label_confirm_password_error"))
      .oneOf([Yup.ref("newPassword"), null], t("password_validation_does_not_match")),
  });
  const otpValidationSchema = Yup.object().shape({
    otp: Yup.string()
      .required(t("validation_OTP_is_required"))
      .length(4, t("otp_error_msg"))
      .matches(/^\d{4}$/, t("invalid_otp")),
  });

  const restaurantProfileValidationSchema = Yup.object({
    restaurantName: Yup.string().required('label_restaurants_error'),
    restaurantAddress: Yup.string().required('restaurant_address_error'),
    city: Yup.string().required('label_city_error'),
    zipCode: Yup.string().required('zip_code_error'),
    website: Yup.string().url('invalid_web_url'),
    cuisineType: Yup.array().min(1, 'label_cuisine_type_error').required('label_cuisine_type_error'),
    openingTime: Yup.string().required('opening_time_error'),
    closingTime: Yup.string().required('closing_time_error').test('greater_time', 'rest_validation_closing_time_should_be_greater_than_opening_time', function (value) {
      // const [opHours, opMins] = this.parent?.openingTime?.split(":");
      // const [closeHours, closeMins] = value?.split(":");
      // if(opHours > closeHours){
      //   return false;
      // }else if(opHours === closeHours){
      //   if(opMins >= closeMins){
      //     return false;
      //   }
      // }
      // return true;
      if (this.parent?.openingTime && value) {
        try {
          const openDate = new Date(this.parent?.openingTime);
          const closeDate = new Date(value);
          return closeDate.getTime() > openDate.getTime();
        } catch (err) {
          console.log("err", err);
        }
      }
      return true;
    }),
  });

  const profileValidationSchema = Yup.object({
    email: Yup.string().matches(EMAIL_REGEX, 'invalid_email').required('label_email_error'),
    opening: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    settlement: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    cancel_sales: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
    edit_sales: Yup.number().oneOf([0, 1], 'Value must be 0 or 1').required('This field is required'),
  });


  const CashierPasswordValidationSchema = Yup.object({
    cashier_password: Yup.string()
      .required("validation_password_required")
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        "password_validation_lowercase"
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        "Password_Validation_uppercase"
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        "Password_Validation_number"
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        "Password_Validation_special_char"
      )
      .min(PASSWORD_MIN_LENGTH, "Password_Validation_minimum_length")
      .max(
        PASSWORD_MAX_LENGTH,
        "Password_Validation_maximum_length"
      )
  });


  const categoryValidationSchema = Yup.object({
    name: Yup.string().required("category_name_error"),
    name_ja: Yup.string().required("category_name_ja_error"),
    tax_type: Yup.string().required("tax_type_error"),
    color: Yup.string().required("color_error"),
    short_description: Yup.string().required("label_short_description_error"),
    short_description_ja: Yup.string().required("label_short_description_japanese_error"),
  });

  const subCategoryValidationSchema = Yup.object({
    category_id: Yup.string().required("category_id_required"),
    name: Yup.string().required("sub_category_name_error"),
    name_ja: Yup.string().required("sub_category_name_ja_error"),
    color: Yup.string().required("color_error"),
    short_description: Yup.string().required("label_short_description_error"),
    short_description_ja: Yup.string().required("label_short_description_japanese_error"),
  });


  const menuItemsValidationSchema = Yup.object().shape({
    item_name: Yup.string()
      .required('item_name_required'),
    item_name_ja: Yup.string()
      .required('item_name_ja_required'),
    category_id: Yup.string()
      .required('category_id_required'),
    item_code: Yup.string()
      .required('item_code_required'),
    short_description: Yup.string()
      .required('short_description_required'),
    short_description_ja: Yup.string()
      .required('short_description_ja_required'),
    price: Yup.number()
      .integer('price_must_be_whole_number')
      .required('price_required')
      .min(0, 'price_min_zero'),

    cost_price: Yup.number()
      .integer('cost_price_must_be_whole_number')
      .required('cost_price_required')
      .min(0, 'cost_price_min_zero')
      .max(100000, 'price_max_1_lakh'),

    consumption_tax_rate: Yup.number()
      .required('consumption_tax_rate_required')
      .min(0, 'consumption_tax_rate_min_zero')
      .max(100, 'consumption_tax_rate_max_100'),
    spice_level_needed: Yup.boolean()
      .required('spice_level_needed_required'),
    image: Yup.mixed()
      .required('label_image_error')
      .test(
        'fileType',
        'supported_file_error',
        value =>
          !value ||
          typeof value === 'string' ||
          ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type)
      )
      .test(
        'fileSize',
        'image_max_size_error',
        value =>
          !value ||
          typeof value === 'string' ||
          value.size <= 1.5 * 1024 * 1024
      )
      .test(
        'isValidUrlOrFile',
        'invalid_url_error',
        value => !value || typeof value !== 'string' || /^(ftp|http|https):\/\/[^ "]+$/.test(value)
      ),
    type: Yup.string()
      .required('type_required'),
    // A set item needs at least one choice group: the order picker offers only
    // the groups configured here, so a set with none has nothing to select and
    // cannot be completed.
    addons: Yup.array().when('type', ([type], schema) => {
      return type === "set_of_items" ? Yup.array().min(1, 'addons_minimum_one_required').of(
        Yup.object().shape({
          option_id: Yup.string().required('addon_required_error'),
          item_id: Yup.string().required('addon_item_required_error'),
        })
      ) : schema;
    }),
    item_variation: Yup.string()
      .required('item_variation_required'),


  });


  const staffValidationSchema = Yup.object({
    employee_id: Yup.string().trim().required("employee_id_error"),
    first_name: Yup.string().trim().required("label_first_name_error"),
    last_name: Yup.string().trim().required("label_last_name_error"),
    staff_joining_date: Yup.string().trim().required("label_joining_date_error"),
    staff_state: Yup.string(),
    staff_zip_code: Yup.string(),
    staff_city: Yup.string(),
    address: Yup.string().trim(),
    email: Yup.string().trim().required('label_email_error').matches(EMAIL_REGEX, 'invalid_email'),
    phone_number: Yup.string()
      .test('phone_required', 'label_phone_number_required_error', (value) => {
        console.log("value", value)
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),
    password: Yup.string()
      .required(t("validation_password_required"))
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("Password_Validation_maximum_length")
      ),
  });


  const editStaffValidationSchema = Yup.object({
    employee_id: Yup.string().trim().required("employee_id_error"),
    first_name: Yup.string().trim().required("label_first_name_error"),
    last_name: Yup.string().trim().required("label_last_name_error"),
    staff_joining_date: Yup.string().trim().required("label_joining_date_error"),
    staff_state: Yup.string(),
    staff_zip_code: Yup.string(),
    staff_city: Yup.string(),
    address: Yup.string().trim(),
    email: Yup.string().trim().required('label_email_error').matches(EMAIL_REGEX, 'invalid_email'),
    phone_number: Yup.string()
      .test('phone_required', 'label_phone_number_required_error', (value) => {
        console.log("value", value)
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),
    password: Yup.string()
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        t("password_validation_lowercase")
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        t("Password_Validation_uppercase")
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        t("Password_Validation_number")
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        t("Password_Validation_special_char")
      )
      .min(PASSWORD_MIN_LENGTH, t("Password_Validation_minimum_length"))
      .max(
        PASSWORD_MAX_LENGTH,
        t("Password_Validation_maximum_length")
      ),
  });


  const tableValidationSchema = Yup.object({
    table_number: Yup.string().trim().required("table_number_error").max(TITLE_MAX_LIMIT, "max_character_length_error"),

    capacity: Yup.string().trim()
      .required("table_capacity_error")
      .test('capacity_digit_error', 'number_err_msg', (value) => {
        const capacity = value ? parseFloat(value) : null;
        return capacity !== null && capacity >= 0; // Check if the value is non-negative
      })
      .test('capacity_digit_error', 'label_capacity_digits_error', (value) => {
        const capacity = value ? parseFloat(value) : null;
        return capacity !== null && Number.isInteger(capacity); // Check if the value is an integer
      })
      .test('capacity_max_error', 'table_capacity_max_error', (value) => {
        const capacity = value ? parseFloat(value) : null;
        return capacity !== null && capacity <= TABLE_CAPACITY_MAX; // Check within capacity limit
      }),

    shape: Yup.string().trim().required('table_shape_error')
  });

  const serviceChargeValidationSchema = Yup.object().shape({
    service_charge: Yup.string()
      .trim()
      .required("service_charge_required")  // Required field
      .test('is-non-negative', 'service_charge_negative_error', (value) => {
        const serviceCharge = value ? parseFloat(value) : null;
        return serviceCharge !== null && serviceCharge >= 0 && Number.isInteger(serviceCharge);  // Check non-negative and integer
      })
      .test('is-within-range', 'service_charge_limit_error', (value) => {
        const serviceCharge = value ? parseInt(value) : null;
        return serviceCharge !== null && serviceCharge >= 0 && serviceCharge <= 100;  // Check within range 0 to 100
      })
  });



  const addResevationValidationSchema = Yup.object().shape({
    table_id: Yup.string().trim().required("table_error"),
    table_number: Yup.string().trim(),
    number_of_guest: Yup.string().trim()
      .required("table_capacity_error")
      .test('capacity_digit_error', 'number_err_msg', (value) => {
        const capacity = value ? parseFloat(value) : null;
        return capacity !== null && capacity > 0; // Check if the value is non-negative
      })
      .test('capacity_digit_error', 'label_capacity_digits_error_guests', (value) => {
        const capacity = value ? parseFloat(value) : null;
        return capacity !== null && Number.isInteger(capacity); // Check if the value is an integer
      }),
    customer_name: Yup.string().trim().required("label_customer_name_error"),
    customer_email: Yup.string().trim().required('label_email_error').matches(EMAIL_REGEX, 'invalid_email'),
    customer_phone_number: Yup.string()
      .test('phone_required', 'label_phone_number_required_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length;
      }
      )
      .test('phone_digit_error', 'label_phone_number_digits_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && /^\d+$/.test(phoneNumber);
      }
      )
      .test('phone_min_length', 'label_phone_number_min_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
      }
      )
      .test('phone_max_length', 'label_phone_number_max_length_error', (value) => {
        const phoneNumber = value ? (value.split(" "))[1] : null;
        return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
      }
      ),

    reservation_date: Yup.string().trim().required("label_date_error"),
    start_time: Yup.string().trim().required("label_start_time_error"),
    end_time: Yup.string()
      .required('label_end_time_error')
      .test('greater_time', 'rest_validation_end_time_should_be_greater_than_start_time', function (value) {
        if (this.parent?.start_time && value) {
          try {
            const openDate = new Date(this.parent?.start_time);
            const closeDate = new Date(value);
            openDate.setSeconds(0);
            openDate.setMilliseconds(0);
            closeDate.setSeconds(0);
            closeDate.setMilliseconds(0);
            return closeDate.getTime() > openDate.getTime();
          } catch (err) {
            console.log("err", err);
          }
        }
        return true;
      })
      .test('min_duration', 'rest_validation_reservation_min_duration', function (value) {
        if (this.parent?.start_time && value) {
          try {
            const openDate = new Date(this.parent?.start_time);
            const closeDate = new Date(value);
            const durationMinutes = (closeDate.getTime() - openDate.getTime()) / 60000;
            return durationMinutes >= MIN_RESERVATION_DURATION_MINUTES;
          } catch (err) {
            console.log("err", err);
          }
        }
        return true;
      }),
  });



  const addVendorValidationSchema = Yup.object().shape({
    companyName: Yup.string()
      .trim()
      .matches(/^[A-Za-z0-9][A-Za-z0-9\s]*$/, "Invalid format")
      .required("Company name is required"),

    contactPersonName: Yup.string()
      .trim()
      .matches(/^[A-Za-z0-9][A-Za-z0-9\s]*$/, "Invalid format")
      .required("Contact person name is required"),

    telephoneNumber: Yup.string()
      .required("Phone number is required")
      .matches(/^[0-9]{10}$/, "Phone number must be 6 to 10 digit"),

    email: Yup.string()
      .trim()
      .required("Email is required")
      .email("Invalid email"),

    address: Yup.string()
      .trim()
      .matches(/^[A-Za-z0-9].*$/, "Invalid format")
      .required("Address is required"),

    notes: Yup.string()
      .trim()
      .matches(/^[A-Za-z0-9].*$/, "Invalid format")
      .required(t("Notes are required"))
      .max(500, t("Notes cannot exceed 500 characters")),
  });


  const editSlipValidationSchema = Yup.object().shape({
    transaction_id: Yup.string().trim().required("Transaction ID is required"),

    //validation for number of persons
    number_of_persons: Yup.number()
      .required("All count is required")
      .integer("All count must be a whole number")
      .min(0, "All count cannot be negative")
      .test(
        "all-valid",
        "All count cannot be in decimal",
        (value) => Number.isInteger(value) && value >= 0
      ),

    // Validation for men count
    men: Yup.number()
      .required("Men count is required")
      .integer("Men count must be a whole number")
      .min(0, "Men count cannot be negative")
      .max(Yup.ref("number_of_persons"), "Men count cannot exceed total number of persons")
      .test(
        "men-valid",
        "Men count cannot be in decimal",
        (value) => Number.isInteger(value) && value >= 0
      ),

    // Validation for women count
    women: Yup.number()
      .required("Women count is required")
      .integer("Women count must be a whole number")
      .min(0, "Women count cannot be negative")
      .max(Yup.ref("number_of_persons"), "Women count cannot exceed total number of persons")
      .test(
        "women-valid",
        "Women count cannot be in decimal",
        (value) => Number.isInteger(value) && value >= 0
      ),

    payment_method: Yup.string().trim().required("Payment method is required"),
    type: Yup.string().trim().required("Type is required"),
    cashier_id: Yup.string().trim().required("Cashier ID is required"),
    table_id: Yup.string().trim().required("Table ID is required"),
    comment: Yup.string()
      .trim()
      .max(500, "Comment must not exceed 500 characters")
      .optional(),

    ordered_items: Yup.array().of(
      Yup.object().shape({
        quantity: Yup.number()
          .required("Quantity is required")
          .integer("Quantity must be a whole number")
          .min(0, "Quantity cannot be negative")
          .max(200, "Maximum quantity allowed is 200"),

        price: Yup.number()
          .required("Price is required")
          .min(0, "Price cannot be negative")
          .max(100000, "Maximum price allowed is 1 lakh")
          .test(
            "is-whole-number",
            "Price must be a whole number",
            (value) => Number.isInteger(value)
          ),

        temp_item_id: Yup.string()
          .trim()
          .required("Item ID is required"),

        order_id: Yup.string()
          .trim()
          .required("Order ID is required"),
      })
    ),
  });


  const validatePassword = Yup.object().shape({
    password: Yup.string()
      .required(t("validation_password_required"))
  });


  const restaurantAddValidationSchema = Yup.object().shape({
    restaurant_name: Yup.string().trim().max(100, "max_length_error").required("label_restaurant_name_error"),
    email: Yup.string()
      .trim()
      .email("label_email_invalid_format_error")
      .test('no-uppercase', "label_email_uppercase_error", function (value) {
        if (value) {
          return !/[A-Z]/.test(value);
        }
        return true;
      })
      .required("label_email_required_error"),
    phone_number: PHONE_VALIDATION,
    address: Yup.string().trim().max(TEXTAREA_MAX_LENGTH, "validation_max_input_characters").required("label_location_error"),
    profile_image: Yup.mixed()
      .nullable()
      .test(
        'fileType',
        'supported_file_error',
        value => !value || ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type)
      )
      .test(
        'fileSize',
        'image_max_size_error',
        value => !value || value.size <= 1.5 * 1024 * 1024
      ),
    cuisine_type: Yup.array()
      .of(Yup.string().trim().required("label_cuisine_type_error"))
      .required("label_cuisine_type_error")
      .min(1, "At least one cuisine type is required")
    ,
  });

  const editOrderValidationSchema = Yup.object().shape({
    vendor_id: Yup.string()
      .required("Vendor is required"),

    order_date: Yup.date()
      .nullable()
      .required("Order date is required")
      .typeError("Invalid date"),

    order_amount: Yup.number()
      .typeError("Order amount must be a number")
      .required("Order amount is required")
      .min(1, "Order amount must be greater than 0"),

    payment_status: Yup.string()
      .required("Payment status is required"),

    delivery_status: Yup.string()
      .required("Delivery status is required"),

    products: Yup.array()
      .min(1, "At least one product is required")
      .of(
        Yup.object().shape({
          product_name: Yup.string()
            .trim()
            .required("Product name is required"),

          unit: Yup.string()
            .trim()
            .required("Unit is required"),

          quantity: Yup.number()
            .typeError("Quantity must be a number")
            .required("Quantity is required")
            .min(1, "Quantity must be at least 1"),
        })
      ),
  });



  const addOrderValidationSchema = Yup.object().shape({
    vendor_id: Yup.string()
      .trim()
      .required("Vendor is required"),

    order_date: Yup.date()
      .typeError("Invalid date")
      .required("Order date is required"),


    delivery_status: Yup.string()
      .trim()
      .required("Delivery status is required"),

    order_amount: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .typeError("Order amount must be a number")
      .required("Order amount is required")
      .min(1, "Order amount must be greater than 0"),

    products: Yup.array()
      .min(1, "At least one product is required")
      .of(
        Yup.object().shape({
          product_name: Yup.string()
            .trim()
            .required("Product name is required"),

          unit: Yup.string()
            .trim()
            .required("Unit is required"),

          quantity: Yup.number()
            .transform((value, originalValue) =>
              originalValue === "" ? undefined : value
            )
            .typeError("Quantity must be a number")
            .required("Quantity is required")
            .min(1, "Quantity must be at least 1"),
        })
      ),
  });


  const spiceAddValidationSchema = Yup.object({
    name: Yup.string().trim().max(20, "label_name_en_max_error").required("label_name_en_error"),
    name_ja: Yup.string().trim().max(20, "label_name_ja_max_error").required("label_name_ja_error"),
  });

  const spiceEditValidationSchema = Yup.object({
    name: Yup.string().trim().max(20, "label_name_en_max_error").required("label_name_en_error"),
    name_ja: Yup.string().trim().max(20, "label_name_ja_max_error").required("label_name_ja_error"),
  });

















  // number_err_msg label_capacity_digits_error


  return {
    loginValidationSchema,
    registerValidationSchema,
    forgetPasswordValidationSchema,
    resetPswValidationSchema,
    otpValidationSchema,
    restaurantProfileValidationSchema,
    CashierPasswordValidationSchema,
    profileValidationSchema,
    categoryValidationSchema,
    subCategoryValidationSchema,
    menuItemsValidationSchema,
    staffValidationSchema,
    editStaffValidationSchema,
    tableValidationSchema,
    serviceChargeValidationSchema,
    addResevationValidationSchema,
    editSlipValidationSchema,
    validatePassword,
    restaurantAddValidationSchema,
    addVendorValidationSchema,
    editOrderValidationSchema,
    addOrderValidationSchema,
    spiceAddValidationSchema,
    spiceEditValidationSchema 
  };
};
